import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable, type Column } from "@/components/ui/table";
import { CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";

interface TraceabilityReportProps {
  /** List of work item traceability items */
  items: Array<{
    workItemKey: string;
    workItemTitle: string;
    testCount: number;
    passedCount: number;
    failedCount: number;
    blockedCount: number;
    coverageStatus: "covered" | "failing" | "blocked" | "untested" | "no_tests";
  }>;
}

/** Coverage status badge component */
function CoverageStatusBadge({
  status,
}: {
  status: TraceabilityReportProps["items"][0]["coverageStatus"];
}) {
  const configs = {
    covered: { label: "Covered", variant: "success" as const, icon: CheckCircle2 },
    failing: { label: "Failing", variant: "destructive" as const, icon: XCircle },
    blocked: { label: "Blocked", variant: "warning" as const, icon: Clock },
    untested: { label: "Untested", variant: "info" as const, icon: Clock },
    no_tests: { label: "No tests", variant: "destructive" as const, icon: AlertCircle },
  };

  const config = configs[status];
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

/** Summary cards for traceability */
function TraceabilitySummary({ items }: TraceabilityReportProps) {
  const total = items.length;
  const covered = items.filter((item) => item.coverageStatus === "covered").length;
  const failing = items.filter((item) => item.coverageStatus === "failing").length;
  const blocked = items.filter((item) => item.coverageStatus === "blocked").length;
  const _untested = items.filter((item) => item.coverageStatus === "untested").length;
  const noTests = items.filter((item) => item.coverageStatus === "no_tests").length;

  const coverageRate = total > 0 ? Math.round((covered / total) * 100) : 0;

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Work Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{total}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Covered</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{covered}</div>
          <div className="text-xs text-muted-foreground">
            {total > 0 ? `${coverageRate}% coverage` : "0% coverage"}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Failing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">{failing}</div>
          <div className="text-xs text-muted-foreground">Need attention</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Blocked</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-yellow-600">{blocked}</div>
          <div className="text-xs text-muted-foreground">Dependencies</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">No Tests</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">{noTests}</div>
          <div className="text-xs text-muted-foreground">Flagged for test creation</div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Main traceability report component */
export function TraceabilityReport({ items }: TraceabilityReportProps) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <div className="text-muted-foreground">No work items found for this project</div>
            <div className="text-sm text-muted-foreground">
              Work items traceability shows mapping from requirements to test cases and results.
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: Column<(typeof items)[0]>[] = [
    {
      key: "workItem",
      header: "Work Item",
      cell: (item) => (
        <div>
          <div className="font-medium">{item.workItemKey}</div>
          <div className="text-sm text-muted-foreground mt-1">{item.workItemTitle}</div>
        </div>
      ),
    },
    {
      key: "tests",
      header: "Tests",
      mono: true,
      cell: (item) => (
        <div className="font-mono text-sm">
          {item.testCount} test{item.testCount !== 1 ? "s" : ""}
        </div>
      ),
    },
    {
      key: "passed",
      header: "Passed",
      cell: (item) => <div className="text-green-600 font-medium">{item.passedCount}</div>,
    },
    {
      key: "failed",
      header: "Failed",
      cell: (item) => <div className="text-red-600 font-medium">{item.failedCount}</div>,
    },
    {
      key: "blocked",
      header: "Blocked",
      cell: (item) => <div className="text-yellow-600 font-medium">{item.blockedCount}</div>,
    },
    {
      key: "coverage",
      header: "Coverage Status",
      cell: (item) => <CoverageStatusBadge status={item.coverageStatus} />,
    },
  ];

  return (
    <div className="space-y-6">
      <TraceabilitySummary items={items} />

      <Card>
        <CardHeader>
          <CardTitle>Work Item Traceability Matrix</CardTitle>
          <CardDescription>
            Mapping from work items to their proving test cases and results
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            caption="Work Item Traceability Matrix"
            columns={columns}
            rows={items}
            getRowId={(item) => item.workItemKey}
            pageSize={20}
          />
        </CardContent>
      </Card>
    </div>
  );
}
